import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { PasswordInput } from "@/components/PasswordInput";
import { SearchableMultiSelect } from "@/components/SearchableMultiSelect";
import { Switch } from "@/components/Switch";
import { userManagementApi, roleManagementApi, tenantApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { validatePassword } from "@/utils/passwordPolicy";

export interface EditUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (updatedUser: unknown) => void;
    user: {
        id: number;
        name: string;
        email: string;
        needs_password_change?: boolean;
        roles?: { id: number; name: string; tag?: string }[];
        companies?: { id: number; name: string }[];
    } | null;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    user,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();
    const activeCompanyId = localStorage.getItem("vigepra_active_company");
    const isMainCompany = !activeCompanyId || activeCompanyId === "main";

    const [name, setName] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [needsPasswordChange, setNeedsPasswordChange] =
        useState<boolean>(false);

    const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
    const [selectedCompanies, setSelectedCompanies] = useState<number[]>([]);

    const [roles, setRoles] = useState<
        { id: number; name: string; originalTag?: string }[]
    >([]);
    const [companies, setCompanies] = useState<{ id: number; name: string }[]>(
        []
    );
    const [loading, setLoading] = useState<boolean>(false);

    const [nameError, setNameError] = useState<boolean>(false);
    const [emailError, setEmailError] = useState<boolean>(false);

    const nameRef = useRef<HTMLInputElement>(null);
    const emailRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen && user) {
            setName(user.name || "");
            setEmail(user.email || "");
            setPassword("");
            setNeedsPasswordChange(!!user.needs_password_change);

            setSelectedRoles(user.roles ? user.roles.map((r) => r.id) : []);
            setSelectedCompanies(
                user.companies
                    ? Array.from(new Set(user.companies.map((c) => c.id)))
                    : []
            );

            setNameError(false);
            setEmailError(false);

            const fetchData = async (): Promise<void> => {
                try {
                    const userResponse = await userManagementApi.get(user.id);
                    const detailedUser = userResponse.data;

                    if (detailedUser) {
                        setSelectedRoles(detailedUser.roles?.map((role) => role.id) || []);
                        setSelectedCompanies(
                            Array.from(
                                new Set(detailedUser.companies?.map((company) => company.id) || [])
                            )
                        );
                    }

                    if (!isMainCompany) {
                        const rolesRes = await roleManagementApi.getAll();
                        if (rolesRes.result) {
                            setRoles(
                                rolesRes.data.map(
                                    (r: {
                                        id: number;
                                        name: string;
                                        tag?: string;
                                    }) => {
                                        const tagKey = `roles.tags.${(r.tag || r.name).toLowerCase()}`;
                                        const translated = t(tagKey);
                                        const displayName =
                                            translated !== tagKey
                                                ? translated
                                                : r.name;
                                        return {
                                            id: r.id,
                                            name: displayName,
                                            originalTag: r.tag,
                                        };
                                    }
                                )
                            );
                        }
                    } else {
                        const companiesRes = await tenantApi.getAll();
                        if (
                            companiesRes.result &&
                            companiesRes.data.companies
                        ) {
                            setCompanies(
                                companiesRes.data.companies.map(
                                    (c: { id: number; name?: string }) => ({
                                        id: c.id,
                                        name: c.name || String(c.id),
                                    })
                                )
                            );
                        }
                    }
                } catch {
                    // ignore error
                }
            };
            fetchData();
        }
    }, [isOpen, user, isMainCompany, t]);

    const handleSubmit = async (): Promise<void> => {
        if (!user) {
            return;
        }

        const missingName = !name.trim();
        const missingEmail = !email.trim();

        if (missingName || missingEmail) {
            setNameError(missingName);
            setEmailError(missingEmail);

            showError(t("developments.modal_new.validation_required"));

            const firstMissing = missingName ? nameRef : emailRef;
            firstMissing.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });

            setTimeout(() => {
                setNameError(false);
                setEmailError(false);
            }, 3000);
            return;
        }

        if (password) {
            const passwordErrorKey = validatePassword(password);
            if (passwordErrorKey) {
                showError(t(passwordErrorKey));
                return;
            }
        }

        setLoading(true);
        try {
            const payload: {
                name: string;
                email: string;
                password?: string;
                needs_password_change: boolean;
                roles?: number[];
                companies?: number[];
            } = {
                name,
                email,
                needs_password_change: needsPasswordChange,
            };

            if (password) {
                payload.password = password;
            }

            if (!isMainCompany) {
                payload.roles = selectedRoles;
            } else {
                payload.companies = selectedCompanies;
            }

            const res = await userManagementApi.update(user.id, payload);
            showSuccess(t("users.updated"));
            onSuccess(res.data);
            onClose();
        } catch (error: unknown) {
            const err = error as { message?: string };
            showError(err.message || t("users.error_update"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("users.edit_title")}
            onSubmit={handleSubmit}
            submitLabel={t("common.save")}
            submittingLabel={t("common.saving")}
            cancelLabel={t("common.cancel")}
            isSubmitting={loading}
            maxWidth="md"
        >
            <div className="space-y-4 p-4">
                <div ref={nameRef}>
                    <Input
                        label={t("users.name")}
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            if (nameError) setNameError(false);
                        }}
                        required
                        className={
                            nameError ? "animate-error-blink rounded-xl" : ""
                        }
                    />
                </div>
                <div ref={emailRef}>
                    <Input
                        label={t("users.email")}
                        type="email"
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            if (emailError) setEmailError(false);
                        }}
                        required
                        className={
                            emailError ? "animate-error-blink rounded-xl" : ""
                        }
                    />
                </div>
                <div>
                    <PasswordInput
                        label={
                            t("users.password") +
                            " (" +
                            t("common.optional") +
                            ")"
                        }
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t("users.password_placeholder")}
                    />
                </div>
                <div>
                    <Switch
                        checked={needsPasswordChange}
                        onChange={setNeedsPasswordChange}
                        label={t("users.temporary_password")}
                        description={t("users.temporary_password_desc")}
                    />
                </div>

                {!isMainCompany && (
                    <SearchableMultiSelect
                        className="mb-4"
                        label={t("users.roles")}
                        helperText={t("users.roles_company_hint")}
                        options={roles}
                        value={selectedRoles}
                        onChange={setSelectedRoles}
                        placeholder={t("common.search")}
                        selectionControl="switch"
                    />
                )}

                {isMainCompany && (
                    <SearchableMultiSelect
                        label={t("companies.title")}
                        options={companies}
                        value={selectedCompanies}
                        onChange={setSelectedCompanies}
                        placeholder={t("common.search")}
                        selectionControl="switch"
                    />
                )}
            </div>
        </Modal>
    );
};
